/**
 * Notification Service
 * 
 * Handles all API calls related to notifications:
 * - Fetching notifications
 * - Getting unread count
 * - Marking as read
 * - Deleting notifications
 */

import { configService } from './configService';
import { buildApiUrl } from '../config/apiConfig';

const getApiBaseUrl = () => configService.getApiUrl();

/** Routes /notifications/* sont sur l’API secondaire en natif — même logique que apiService */
const notificationsUrl = (path: string): string =>
  buildApiUrl(path.startsWith('/') ? path : `/${path}`);

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

/**
 * Create headers with authentication
 */
const getHeaders = (): HeadersInit => {
    const token = getAuthToken();
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

/**
 * Notification interface matching backend response
 */
export interface Notification {
    id: number;
    type: string;
    title: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    readAt: string | null;
    actionType: string | null;
    actionData: {
        path?: string;
        booking_id?: number;
        forfait_id?: number;
        event_id?: number;
        message_id?: string;
        [key: string]: any;
    } | null;
    bookingId: number | null;
    forfaitId: number | null;
    eventId: number | null;
    messageId: string | null;
}

const EVENT_UNIQUE_LIST = '/app/services?filter=ponctuel';
const STALE_EVENEMENTS = '/app/evenements';
const STALE_BACKOFFICE_RESERVATION = /^\/backoffice\/reservations\/(\d+)\/?$/;
const CLIENT_PATH_PREFIXES = ['/reservations', '/messaging', '/quotes'];
const EVENT_TYPES = new Set(['payment_received', 'event_registration', 'event_unsubscription']);
const FORFAIT_TYPES = new Set(['forfait_purchased', 'forfait_expiring', 'credit_pack_purchased']);
const SUBSCRIPTION_TYPES = new Set([
    'subscription_cancelled',
    'boarding_subscription_cancelled',
    'subscription_ending',
]);
const STAY_TYPES = new Set([
    'new_stay_booking',
    'stay_booking_confirmed',
    'stay_booking_cancelled',
]);
const QUOTE_TYPES = new Set(['quote_accepted', 'quote_rejected']);

function eventUniquePath(eventId: number | string | null | undefined): string {
    const n = Number(eventId);
    if (Number.isFinite(n) && n > 0) {
        return `${EVENT_UNIQUE_LIST}&eventId=${n}`;
    }
    return EVENT_UNIQUE_LIST;
}

/**
 * Résout la destination backoffice d'une notification.
 * Réécrit les chemins morts (/app/evenements, /backoffice/reservations/…)
 * et complète un fallback par type si le path manque.
 */
export const resolveNotificationPath = (notification: Notification): string | null => {
    const type = (notification.type || '').trim();
    const rawPath = (notification.actionData?.path || '').trim();
    const eventId =
        notification.eventId ??
        notification.actionData?.event_id ??
        notification.actionData?.eventId;
    const bookingId =
        notification.bookingId ??
        notification.actionData?.bookingId ??
        notification.actionData?.booking_id;
    const conversationSid =
        notification.actionData?.conversation_sid ?? notification.messageId ?? undefined;

    if (rawPath) {
        if (CLIENT_PATH_PREFIXES.some((prefix) => rawPath.startsWith(prefix))) {
            return rawPath;
        }
        if (rawPath === STALE_EVENEMENTS || rawPath.startsWith(`${STALE_EVENEMENTS}?`)) {
            return eventUniquePath(eventId);
        }
        const backofficeMatch = rawPath.match(STALE_BACKOFFICE_RESERVATION);
        if (backofficeMatch) {
            return `/app/booking/${backofficeMatch[1]}`;
        }
        if (rawPath.startsWith('/app/') || rawPath.startsWith('/backoffice/')) {
            return rawPath;
        }
    }

    if (EVENT_TYPES.has(type)) {
        return eventUniquePath(eventId);
    }
    if (type === 'new_message') {
        return conversationSid ? `/app/messages/${conversationSid}` : '/app/messages';
    }
    if (FORFAIT_TYPES.has(type)) {
        const clientId = notification.actionData?.clientId ?? notification.actionData?.client_id;
        if (clientId != null) {
            return `/app/clients/${clientId}`;
        }
        return '/app/forfaits';
    }
    if (SUBSCRIPTION_TYPES.has(type)) {
        return '/app/subscription';
    }
    if (STAY_TYPES.has(type) && bookingId != null) {
        return `/app/stay/${bookingId}`;
    }
    if (QUOTE_TYPES.has(type)) {
        const quoteId = notification.actionData?.quote_id ?? notification.actionData?.quoteId;
        const n = Number(quoteId);
        if (Number.isFinite(n) && n > 0) {
            return `/app/quotes?quote_id=${n}`;
        }
        return '/app/quotes';
    }
    if (bookingId != null) {
        return `/app/booking/${bookingId}`;
    }
    if (eventId != null) {
        return eventUniquePath(eventId);
    }
    if (notification.forfaitId != null) {
        return '/app/forfaits';
    }
    return null;
};

/**
 * Get notifications for the current user
 * @param limit - Number of notifications to fetch (default: 10)
 * @param offset - Offset for pagination (default: 0)
 * @param unreadOnly - Fetch only unread notifications (default: false)
 */
export const getNotifications = async (
    limit: number = 10,
    offset: number = 0,
    unreadOnly: boolean = false
): Promise<{ notifications: Notification[], total: number }> => {
    try {
        const params = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
            ...(unreadOnly && { unread_only: 'true' })
        });

        const response = await fetch(
            `${notificationsUrl('/notifications')}?${params}`,
            {
                method: 'GET',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch notifications: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching notifications:', error);
        throw error;
    }
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (): Promise<number> => {
    try {
        const response = await fetch(
            notificationsUrl('/notifications/unread-count'),
            {
                method: 'GET',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch unread count: ${response.statusText}`);
        }

        const data = await response.json();
        return data.unread_count || 0;
    } catch (error) {
        console.error('Error fetching unread count:', error);
        return 0; // Return 0 on error to avoid breaking UI
    }
};

/**
 * Mark a notification as read
 * @param notificationId - ID of the notification to mark as read
 */
export const markAsRead = async (notificationId: number): Promise<void> => {
    try {
        const response = await fetch(
            notificationsUrl(`/notifications/${notificationId}/mark-read`),
            {
                method: 'POST',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to mark notification as read: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
    }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<void> => {
    try {
        const response = await fetch(
            notificationsUrl('/notifications/mark-all-read'),
            {
                method: 'POST',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to mark all as read: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error marking all as read:', error);
        throw error;
    }
};

/**
 * Delete a notification
 * @param notificationId - ID of the notification to delete
 */
export const deleteNotification = async (notificationId: number): Promise<void> => {
    try {
        const response = await fetch(
            notificationsUrl(`/notifications/${notificationId}`),
            {
                method: 'DELETE',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to delete notification: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error deleting notification:', error);
        throw error;
    }
};

/**
 * Get notification preferences
 */
export const getNotificationPreferences = async (proId?: number | null): Promise<any> => {
    try {
        const url = proId
            ? `${getApiBaseUrl()}/notification-settings?pro_id=${proId}`
            : `${getApiBaseUrl()}/notification-settings`;

        const response = await fetch(
            url,
            {
                method: 'GET',
                headers: getHeaders()
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch preferences: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching notification preferences:', error);
        throw error;
    }
};

/**
 * Update notification preferences
 * @param preferences - Preferences to update
 */
export const updateNotificationPreferences = async (preferences: any): Promise<void> => {
    try {
        const response = await fetch(
            `${getApiBaseUrl()}/notification-settings`,
            {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(preferences)
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to update preferences: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error updating notification preferences:', error);
        throw error;
    }
};

/**
 * Envoie une notification push de test sur les appareils du pro connecté.
 * Pour vérifier immédiatement que les push fonctionnent.
 */
export const sendTestPush = async (): Promise<{
    success: boolean;
    sent?: number;
    total?: number;
    error?: string;
    message?: string;
}> => {
    try {
        const response = await fetch(notificationsUrl('/notifications/test-push'), {
            method: 'POST',
            headers: getHeaders(),
        });
        const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        if (!response.ok) {
            return {
                success: false,
                error: (data.error as string) || (data.message as string) || response.statusText,
                message: data.message as string | undefined,
            };
        }
        return {
            success: true,
            sent: data.sent as number | undefined,
            total: data.total as number | undefined,
            message: data.message as string | undefined,
        };
    } catch (error) {
        console.error('Error sending test push:', error);
        return { success: false, error: 'Erreur réseau' };
    }
};

export default {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationPreferences,
    updateNotificationPreferences,
    sendTestPush
};
